import { useState } from 'react';
import { IconButton, LockIcon, Stack, Text } from '@citibox-panel/ui';
import type { UserSearchHit } from '@citibox-panel/api-client';
import { useNoticeOnError } from '../../search-shell/hooks/useErrorNotice';
import { useBlockUser } from '../hooks/useBlockUser';
import { accountSubject } from '../lib/accountSubject';
import { accountTypeClass, isCourierAccount } from '../lib/accountType';
import { AccountActionDialog } from './AccountActionDialog';

// TODO: i18n — copy en ES hasta Fase 1.
const TRIGGER_LABEL = 'Bloquear';
const DIALOG_LABEL = 'Bloquear cuenta de usuario';
const COURIER_DIALOG_LABEL = 'Bloquear cuenta de mensajero';
const KEEPS_DATA =
  'No se borra ningún dato: nombre, teléfono, email y dirección se conservan, y sus «Entregas» siguen consultables.';
const NO_UNDO = 'Desde el panel no se puede desbloquear.';
// The panel's own sentence, resolved from the fact that the write failed and from nothing the
// server said: `message` and `detail` are attacker-reachable text, and this screen is one an agent
// acts on. Its own id, so a block that failed twice refreshes one notice instead of stacking two —
// and so it never replaces the baja's, which can be on screen at the same time from the same card.
const WRITE_ERROR = 'No se ha podido bloquear la cuenta. Vuelve a intentarlo.';
const NOTICE_ID = 'block-account';
const BLOCKED = 'Cuenta bloqueada';

export interface BlockAccountSectionProps {
  account: UserSearchHit;
  phoneKey: string;
  blockUser: (id: string, reason?: string) => Promise<void>;
  // The block completed and the row is about to lose the control that was pressed. Reported
  // outward rather than handled here, because the row belongs to the card: this section knows
  // WHEN the focus is orphaned, and only its owner knows what is still standing to give it to.
  onBlocked?: () => void;
  // The card's live region, raised to the host. Optional like its sibling above, so a caller with
  // no region to write into — the ui-kit demo — can decline both.
  onAnnounce?: (message: string) => void;
}

// The «Bloquear» action and its confirmation. It lives on the STATUS ROW rather than at the foot of
// the card with the baja, because the criterion is «the action lives where its result is read» and
// what a block changes is the chip right beside it.
//
// The dialog is `AccountActionDialog`, shared with the unblock, because a courier account's block
// has a form: the «Motivo». On a «Usuario» it stays exactly as it was — no field, the focus on
// «Cancelar», and the sentence that the panel cannot undo it, which on a courier account would lie.
export function BlockAccountSection({
  account,
  phoneKey,
  blockUser,
  onBlocked,
  onAnnounce,
}: BlockAccountSectionProps) {
  const [open, setOpen] = useState(false);
  // Called unconditionally, ahead of the gate below: this same instance can be re-rendered with a
  // status that just flipped — another agent, or the automation, getting there first — and the
  // Rules of Hooks forbid skipping a hook call on some renders but not others.
  const mutation = useBlockUser(
    account.id,
    phoneKey,
    blockUser,
    accountTypeClass(account.accountType),
  );
  // Floating and not in the modal (ADR 0021): the failure outlives the dialog, so an agent who
  // closes it to go and check something still has the sentence on screen, and the dialog behind it
  // is left exactly as it was — because the next thing they do is press the button again. Raised
  // while the modal is open, which puts the notice's node outside MUI's `aria-hidden` sweep of
  // every body-level sibling and is why it can be read from inside the dialog at all.
  useNoticeOnError(mutation.isError, NOTICE_ID, WRITE_ERROR);

  // Gated on `active` and NOT on `status`. `status` is an open string the ficha deliberately treats
  // fail-open (an unknown state degrades to a neutral chip), and a field allowed to mean «something
  // we do not recognize» cannot also be the one deciding whether a sanction is offered. `active` is
  // a boolean backend owns, and it is `false` for both populations this has to exclude: an account
  // already blocked, and one anonymized by a baja. The carrier pre-registration shell travels
  // `active: true` and IS offered the action, deliberately.
  if (!account.active) return null;

  const subject = accountSubject(account);
  const courier = isCourierAccount(account.accountType);

  return (
    <>
      {/* A padlock and no word, beside the text «Editar datos» that shares this row: the glyph is
          what tells the two apart, and the error tint is what says which of them is a sanction.
          With no visible label and no tooltip in the façade, `aria-label` is the ONLY place this
          action is spelled — so it is the contract, not a decoration, and a test pins it. */}
      <IconButton
        aria-label={TRIGGER_LABEL}
        color="error"
        size="small"
        onClick={() => {
          setOpen(true);
        }}
      >
        {/* One step under the 18px a small IconButton gives its glyph: the padlock hangs off the
            chip rather than competing with it. */}
        <LockIcon sx={{ fontSize: 16 }} />
      </IconButton>
      <AccountActionDialog
        open={open}
        label={courier ? COURIER_DIALOG_LABEL : DIALOG_LABEL}
        heading={`¿Bloquear la cuenta de ${subject}?`}
        body={
          <Stack spacing={2}>
            <Text>{KEEPS_DATA}</Text>
            {!courier && <Text>{NO_UNDO}</Text>}
          </Stack>
        }
        confirmLabel="Bloquear"
        confirmColor="error"
        withReason={courier}
        isPending={mutation.isPending}
        onCancel={() => {
          setOpen(false);
        }}
        onConfirm={(reason) => {
          mutation.mutate(reason, {
            // Per-call, not the hook's own onSuccess: this one runs strictly AFTER TanStack has
            // awaited that one, which chains the account refetch. By the time it fires the ficha
            // behind the modal already shows the blocked account, so closing here never flashes
            // the live account.
            onSuccess: () => {
              setOpen(false);
              // Both AFTER the refetch has landed: the sentence describes an account that has
              // already come back blocked, and the control the focus is handed to is the one that
              // survived that repaint.
              onAnnounce?.(BLOCKED);
              onBlocked?.();
            },
          });
        }}
      />
    </>
  );
}
